import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import FlightsMap from './Statistics/FlightsMap';
import FlightsSummary from './Statistics/FlightsSummary';
import FlightsStatList from './Statistics/FlightsStatList';

const SharePage = () => {
  const { uid } = useParams();
  const [shareData, setShareData] = useState(null);
  const [allData, setAllData] = useState(null); // Хранит все данные (map, summary, list)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('map');
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchShareData();
      hasFetchedRef.current = true;
    }
    // Set page title for shared view
    document.title = 'Flight Statistics';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const fetchShareData = async () => {
    try {
      const response = await api.get(`/share-links/public/${uid}`);
      const data = response.data;
      
      // Сохраняем базовую информацию и все данные
      setShareData({
        passenger_id: data.passenger_id,
        passenger_name: data.passenger_name,
        flights_type: data.flights_type,
        name: data.name
      });
      
      setAllData({
        mapData: data.mapData,
        summaryData: data.summaryData,
        listData: data.listData
      });
      
      setLoading(false);
    } catch (err) {
      setError('Link not found or access denied');
      setLoading(false);
    }
  };

  const getFlightsTypeLabel = (type) => {
    switch (type) {
      case 1: return 'Past Flights';
      case 2: return 'Future Flights';
      case 9: return 'All Flights';
      default: return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        padding: '20px'
      }}>
        <div style={{
          padding: '20px',
          backgroundColor: '#ffebee',
          color: '#c62828',
          borderRadius: '8px',
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Mobile-friendly styles */}
      <style>{`
        /* Mobile optimizations */
        @media (max-width: 768px) {
          /* Hide all page headers on mobile by default */
          .page-header {
            display: none !important;
          }
          
          /* Show page header only on map page (it has flight type controls) */
          .map-container + .page-header,
          .page-header:has(input[type="radio"][value="completed"]) {
            display: flex !important;
            padding: 8px 12px !important;
            margin: 0 !important;
            background: #f5f5f5 !important;
            border-bottom: none !important;
          }
          
          .page-header h2 {
            display: none !important;
          }
          
          .page-header > div {
            gap: 10px !important;
          }
          
          /* Hide passenger selector on mobile */
          .page-header > div > div:first-child {
            display: none !important;
          }
          
          /* Remove side padding/margins for full-width tables */
          .table-container {
            margin: 0 !important;
            padding: 0 !important;
            border-radius: 0 !important;
            overflow-x: auto !important;
          }
          
          /* Make table scrollable horizontally */
          .table-container table {
            font-size: 13px !important;
          }
          
          /* Reduce padding in table cells */
          .table-container table th,
          .table-container table td {
            padding: 8px 6px !important;
            white-space: nowrap !important;
          }
          
          /* Adjust navigation buttons for mobile */
          .share-nav-buttons {
            padding: 0 !important;
            gap: 0 !important;
          }
          
          .share-nav-buttons > div {
            padding: 0 !important;
            gap: 0 !important;
          }
          
          .share-nav-buttons button {
            padding: 12px 12px !important;
            font-size: 13px !important;
            flex: 1 !important;
            min-width: 0 !important;
            border-radius: 0 !important;
          }
          
          /* Remove border from nav menu */
          .share-nav-buttons {
            border-bottom: none !important;
          }
          
          /* Remove content padding on mobile */
          .share-content {
            padding: 0 !important;
            margin: 0 !important;
          }
          
          /* Summary cards on mobile */
          .summary-container {
            padding: 12px !important;
          }
          
          .summary-container .grid {
            margin-top: 0 !important;
            margin-bottom: 12px !important;
            gap: 12px !important;
          }
          
          /* Ensure cards have bottom margin */
          .summary-container .card {
            margin-bottom: 0 !important;
          }
          
          /* Make yearly chart horizontal on mobile */
          .yearly-chart-container {
            flex-direction: column !important;
            align-items: stretch !important;
            min-height: auto !important;
            gap: 8px !important;
            border-bottom: none !important;
            padding-bottom: 0 !important;
          }
          
          .yearly-chart-item {
            flex-direction: row !important;
            align-items: center !important;
            width: 100% !important;
            gap: 8px !important;
          }
          
          .yearly-chart-value {
            min-width: 30px !important;
            margin-bottom: 0 !important;
            text-align: left !important;
            order: 3 !important;
          }
          
          .yearly-chart-bar-container {
            flex: 1 !important;
            width: auto !important;
            height: 18px !important;
            order: 2 !important;
            border-radius: 2px !important;
            align-items: center !important;
            justify-content: flex-start !important;
          }
          
          .yearly-chart-bar {
            height: 100% !important;
            border-radius: 2px !important;
            width: var(--bar-width, 100%) !important;
          }
          
          .yearly-chart-year {
            min-width: 35px !important;
            margin-top: 0 !important;
            text-align: left !important;
            order: 1 !important;
          }
          
          /* Smaller font for codes in carriers and planes */
          .codes-label {
            font-size: 0.75em !important;
          }
          
          /* Map Popup styling for mobile */
          .leaflet-popup-content-wrapper {
            border-radius: 8px !important;
            padding: 4px !important;
          }
          
          .leaflet-popup-content {
            margin: 8px !important;
            font-size: 13px !important;
            line-height: 1.4 !important;
          }
          
          /* Map container adjustments */
          .map-container {
            margin: 0 !important;
            border-radius: 0 !important;
          }
          
          /* Remove margins from map legend in mobile */
          .page-container > div {
            margin-bottom: 0 !important;
            border-radius: 0 !important;
          }
          
          /* Reduce padding for legend/stats block on mobile */
          .map-stats-block {
            padding: 10px 15px !important;
          }
          
          /* On mobile: hide full text, show short text */
          .nav-btn-full {
            display: none !important;
          }
          .nav-btn-short {
            display: inline !important;
          }
          
          /* Remove border radius from nav buttons on mobile */
          .share-nav-buttons > div {
            border-radius: 0 !important;
            border-left: none !important;
            border-right: none !important;
            border-bottom: none !important;
            box-shadow: none !important;
          }
        }
        
        /* Desktop: show full text, hide short text */
        .nav-btn-short {
          display: none;
        }
        .nav-btn-full {
          display: inline;
        }
      `}</style>
      
      {/* Navigation */}
      <div className="share-nav-buttons" style={{
        position: 'sticky',
        top: 0,
        zIndex: 2000,
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          display: 'flex',
          gap: '5px',
          padding: '10px',
          maxWidth: '1300px',
          margin: '0 auto',
          backgroundColor: 'white',
          borderLeft: '1px solid #e0e0e0',
          borderRight: '1px solid #e0e0e0',
          borderBottom: '1px solid #ddd',
          borderRadius: '0 0 8px 8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }}>
          <button
            onClick={() => setActiveView('map')}
            style={{
              padding: '10px 20px',
              backgroundColor: activeView === 'map' ? '#1976d2' : 'transparent',
              color: activeView === 'map' ? 'white' : '#666',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeView === 'map' ? 'bold' : 'normal'
            }}
          >
            <span className="nav-btn-full">Flights Map</span>
            <span className="nav-btn-short">Map</span>
          </button>
          {/* Hide Summary tab for future-only flights (type 2) */}
          {shareData.flights_type !== 2 && (
            <button
              onClick={() => setActiveView('summary')}
              style={{
                padding: '10px 20px',
                backgroundColor: activeView === 'summary' ? '#1976d2' : 'transparent',
                color: activeView === 'summary' ? 'white' : '#666',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeView === 'summary' ? 'bold' : 'normal'
              }}
            >
              Summary
            </button>
          )}
          <button
            onClick={() => setActiveView('list')}
            style={{
              padding: '10px 20px',
              backgroundColor: activeView === 'list' ? '#1976d2' : 'transparent',
              color: activeView === 'list' ? 'white' : '#666',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeView === 'list' ? 'bold' : 'normal'
            }}
          >
            <span className="nav-btn-full">Flights List</span>
            <span className="nav-btn-short">List</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="share-content" style={{ margin: '0 auto', padding: '20px 20px 15px 20px' }}>
        {activeView === 'map' && allData && (
          <FlightsMap 
            isSharedView={true}
            sharedPassengerId={shareData.passenger_id}
            sharedFlightsType={shareData.flights_type}
            sharedUid={uid}
            sharedMapData={allData.mapData}
          />
        )}
        {activeView === 'summary' && shareData.flights_type !== 2 && allData && (
          <FlightsSummary 
            isSharedView={true}
            sharedPassengerId={shareData.passenger_id}
            sharedFlightsType={shareData.flights_type}
            sharedUid={uid}
            sharedSummaryData={allData.summaryData}
          />
        )}
        {activeView === 'list' && allData && (
          <FlightsStatList 
            isSharedView={true}
            sharedPassengerId={shareData.passenger_id}
            sharedFlightsType={shareData.flights_type}
            sharedUid={uid}
            sharedListData={allData.listData}
          />
        )}
      </div>
    </div>
  );
};

export default SharePage;
